import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSEO } from "../lib/useSEO";
import { CheckCircle, Trash2, Filter, Flag } from "lucide-react";
import AdminNav from "../components/AdminNav";

const FIELD_LABELS = {
  swimming_allowed: "Swimming rules",
  dog_rules: "Dog & pet rules",
  best_time_to_visit: "Best time to visit",
  official_website: "Official website",
  nearby_attractions: "Nearby attractions",
  height_m: "Height",
  address: "Address",
  general: "General",
};

export default function AdminErrorReports() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  useSEO({ title: "Error reports | Admin" });

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await api.get("/admin/error-reports", { params });
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, filter]);

  if (!authLoading && !user) return <Navigate to="/admin/login" replace />;

  const resolve = async (id) => {
    setBusy((b) => ({ ...b, [id]: "ok" }));
    try { await api.post(`/admin/error-reports/${id}/resolve`); await load(); }
    finally { setBusy((b) => ({ ...b, [id]: null })); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this report? This cannot be undone.")) return;
    setBusy((b) => ({ ...b, [id]: "del" }));
    try { await api.delete(`/admin/error-reports/${id}`); await load(); }
    finally { setBusy((b) => ({ ...b, [id]: null })); }
  };

  return (
    <main className="min-h-screen bg-stone-50">
      <AdminNav />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-stone-900 flex items-center gap-2">
              <Flag className="w-7 h-7 text-rose-600" /> Error reports
            </h1>
            <p className="text-stone-500 text-sm mt-1">User-submitted corrections for waterfall data.</p>
          </div>
          <div className="flex items-center gap-2" data-testid="error-reports-filter">
            <Filter className="w-4 h-4 text-stone-400" />
            {["open", "resolved", ""].map((f) => (
              <button
                key={f || "all"}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f || "all"}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold ${filter === f ? "bg-stone-900 text-white" : "bg-white border border-stone-300 text-stone-700 hover:bg-stone-100"}`}
              >
                {f === "" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-stone-500">Loading…</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-stone-500" data-testid="error-reports-empty">
            No reports {filter ? `with status "${filter}"` : ""}.
          </div>
        ) : (
          <div className="space-y-4" data-testid="error-reports-list">
            {items.map((r) => (
              <article key={r.id} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`error-report-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/waterfalls/${r.waterfall_slug}`}
                        target="_blank"
                        className="font-extrabold text-stone-900 hover:underline truncate"
                      >
                        {r.waterfall_name || r.waterfall_slug}
                      </Link>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${r.status === "open" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {r.status}
                      </span>
                    </div>
                    <p className="text-xs text-stone-500 mt-1">
                      Field: <span className="font-semibold text-stone-700">{FIELD_LABELS[r.field] || r.field}</span>
                      {" · "}{new Date(r.created_at).toLocaleString()}
                      {r.reporter_email && <> · <a href={`mailto:${r.reporter_email}`} className="underline">{r.reporter_email}</a></>}
                    </p>
                  </div>
                </div>
                <p className="text-stone-800 leading-relaxed whitespace-pre-line mb-4 text-sm">{r.message}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {r.status !== "resolved" && (
                    <button
                      onClick={() => resolve(r.id)}
                      disabled={busy[r.id]}
                      data-testid={`resolve-${r.id}`}
                      className="btn-at btn-at-green text-sm disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" /> Mark resolved
                    </button>
                  )}
                  <Link
                    to={`/admin/waterfalls/${r.waterfall_id}`}
                    className="px-3 py-1.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-100"
                    data-testid={`edit-${r.id}`}
                  >
                    Open in editor
                  </Link>
                  <button
                    onClick={() => remove(r.id)}
                    disabled={busy[r.id]}
                    data-testid={`delete-${r.id}`}
                    className="px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1 inline" /> Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
